import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview SponsoredTooltip — "?"-triggered sponsored-disclosure popover
 *
 * Per DES-50 / DES-49 (FTC 16 CFR Part 255), the "Sponsored" label rendered
 * by `<SponsoredBadge>` is always visible on its own — this component is
 * strictly supplementary. It renders the small "?" affordance next to the
 * badge and, on click or keyboard activation (Enter / Space), opens a panel
 * with the disclosure copy. It is never hover-triggered.
 *
 * The disclosure copy is a legal-owned slot (DES-49) — this component never
 * hardcodes the claim text. `children` is required so a consumer cannot
 * render the trigger without supplying copy.
 */
import { forwardRef, useCallback, useEffect, useId, useRef, useState, } from 'react';
import { useTheme } from '../../../theme';
import { Eyebrow } from '../Eyebrow';
import { FEATURED_AMBER, FEATURED_AMBER_TEXT, } from '../SponsoredBadge/SponsoredBadge';
const TRIGGER_SIZE = 16;
// Pixel-sampled from the rendered DES-50 spec (same methodology as
// `SponsoredBadge`'s `TIER_COLORS`) — the "?" trigger sits in the card's
// hero-media context, so it stays theme-invariant. Starter and Growth both
// resolve to the same subtle white-glass affordance in the real spec (not a
// tier-tinted one); only Featured+ gets a distinct amber treatment. This is
// deliberately NOT the same value as SponsoredBadge's exported
// `STARTER_SCRIM`/`GROWTH_SKYLINE` (those tint the badge's own background,
// not this trigger) — don't "fix" this into a mismatch by wiring them here.
const GLASS_TRIGGER = { background: '#FFFFFF2E', color: '#FFFFFF' }; // white @ 18%
// lociAmber @ 28% — shared with SponsoredBadge's `featured` tier constants.
const FEATURED_TRIGGER = {
    background: `${FEATURED_AMBER}47`,
    color: FEATURED_AMBER_TEXT,
};
const TRIGGER_COLORS = {
    starter: GLASS_TRIGGER,
    growth: GLASS_TRIGGER,
    featured: FEATURED_TRIGGER,
};
/**
 * `<SponsoredTooltip>` — the "?" disclosure trigger + popover panel.
 */
export const SponsoredTooltip = forwardRef(({ tier, children, heading = "Why you're seeing this", triggerVariant = 'media', className, style, }, forwardedRef) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const panelId = useId();
    const wrapperRef = useRef(null);
    const close = useCallback(() => setOpen(false), []);
    const toggle = useCallback(() => setOpen((prev) => !prev), []);
    const setRefs = useCallback((node) => {
        wrapperRef.current = node;
        if (typeof forwardedRef === 'function') {
            forwardedRef(node);
        }
        else if (forwardedRef) {
            forwardedRef.current = node;
        }
    }, [forwardedRef]);
    // Escape closes the panel; clicking outside the wrapper closes it too.
    // Listeners are only attached while `open` so the very click that opens
    // the panel cannot also be read as an "outside click" on the same tick.
    useEffect(() => {
        if (!open)
            return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                close();
            }
        };
        const handlePointerDown = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                close();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handlePointerDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [open, close]);
    const handleTriggerKeyDown = (event) => {
        // Native <button> activation already fires a click for Enter/Space,
        // but we handle it explicitly (and preventDefault the native action)
        // so behavior is deterministic across real browsers and jsdom tests.
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
        }
    };
    // Stops propagation so the "?" doesn't also fire a parent card's own
    // click handler (WEB-36 embeds this trigger inside clickable
    // Starter-tier cards) — without this, opening the disclosure would
    // simultaneously navigate away from the card before it can be read.
    const handleTriggerClick = (event) => {
        event.stopPropagation();
        toggle();
    };
    // 'surface' swaps the media-context glass/amber trigger for theme-aware
    // neutrals — see the `triggerVariant` prop docs for why.
    const triggerColors = triggerVariant === 'surface'
        ? { background: theme.colors.surfaceAlt, color: theme.colors.text }
        : TRIGGER_COLORS[tier];
    const triggerStyle = {
        width: TRIGGER_SIZE,
        height: TRIGGER_SIZE,
        display: 'inline-grid',
        placeItems: 'center',
        borderRadius: '50%',
        fontSize: 10,
        lineHeight: 1,
        fontFamily: theme.typography.fontFamily,
        fontWeight: 600,
        cursor: 'pointer',
        backgroundColor: triggerColors.background,
        color: triggerColors.color,
        border: triggerVariant === 'surface' ? `1px solid ${theme.colors.borderStrong}` : 'none',
        padding: 0,
        appearance: 'none',
    };
    const wrapperStyle = {
        position: 'relative',
        display: 'inline-flex',
        ...style,
    };
    return (_jsxs("span", { ref: setRefs, className: ['loci-sponsored-tooltip', className].filter(Boolean).join(' '), style: wrapperStyle, "data-testid": "sponsored-tooltip", "data-tier": tier, "data-trigger-variant": triggerVariant, children: [_jsx("button", { type: "button", "aria-expanded": open, "aria-controls": open ? panelId : undefined, "aria-label": "About sponsored listings", "data-testid": "sponsored-tooltip-trigger", style: triggerStyle, onClick: handleTriggerClick, onKeyDown: handleTriggerKeyDown, children: "?" }), open && (_jsxs("div", { id: panelId, style: {
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    zIndex: 20,
                    width: 240,
                    padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.lg,
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.borderStrong}`,
                    boxShadow: theme.shadows.lg,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                }, "data-testid": "sponsored-tooltip-panel", children: [_jsx(Eyebrow, { children: heading }), _jsx("div", { style: {
                            fontSize: theme.typography.fontSize.small,
                            color: theme.colors.text,
                            // Fixed rather than `theme.typography.lineHeights.normal` —
                            // the shared test mock (`notifications/__tests__/utils/themeMock`)
                            // only stubs a singular `lineHeight` map, not the real
                            // `lineHeights` (plural) shape from `theme/design-tokens.ts`.
                            lineHeight: 1.5,
                        }, children: children })] }))] }));
});
SponsoredTooltip.displayName = 'SponsoredTooltip';
